/**
 * 엔글 오목 — 화면 판정 순수 로직.
 *
 * 내 진영: 서버가 UID 로 확인해 준 내 publicPlayerId 를 실시간 방 문서의 흑/백 플레이어와 비교한다.
 *   (방장은 waiting 동안 임시로 흑 자리에 있다가 참가자 입장 시 서버 랜덤으로 백이 될 수 있으므로
 *    입장 시점의 side 를 고정해 두면 안 된다.)
 */
import type { OmokPublicRoom, OmokSide, OmokWinReason, OmokWinner } from './types';

export function resolveMySide(
  room: Pick<OmokPublicRoom, 'playerBlack' | 'playerWhite'> | null,
  myPublicPlayerId: string | null,
): OmokSide | null {
  if (!room || !myPublicPlayerId) return null;
  if (room.playerBlack?.publicPlayerId === myPublicPlayerId) return 'black';
  if (room.playerWhite?.publicPlayerId === myPublicPlayerId) return 'white';
  return null;
}

export type OmokOutcome = 'win' | 'lose' | 'draw' | 'spectator' | 'unknown';

export interface OmokResultView {
  outcome: OmokOutcome;
  text: string;
}

export function sideLabel(side: OmokSide): string {
  return side === 'black' ? '흑돌' : '백돌';
}

/** 승리 사유 문구. 승자/패자 시점에 따라 기권·이탈 표현이 다르다. */
export function reasonLabel(
  winReason: OmokWinReason,
  viewer: 'winner' | 'loser' | 'spectator',
  winningLineLength: number,
): string {
  switch (winReason) {
    case 'five':
      return '5목 완성';
    case 'overline':
      return `${Math.max(6, winningLineLength)}목 완성`;
    case 'resign':
      return viewer === 'winner' ? '상대 기권' : '기권';
    case 'abandon':
      return viewer === 'winner' ? '상대 이탈' : '이탈';
    case 'timeout':
      return viewer === 'winner' ? '상대 시간 초과' : '시간 초과';
    default:
      return '승리';
  }
}

/**
 * 결과 문구.
 *   승자:   "승리 - {승자 진영} 승리 ({사유})"
 *   패자:   "패배 - {승자 진영} 승리 ({사유})"
 *   관전자: "{승자 진영} 승리 ({사유})"
 */
export function buildResultView(
  winner: OmokWinner,
  winReason: OmokWinReason,
  mySide: OmokSide | null,
  winningLineLength = 0,
): OmokResultView {
  if (winner === 'draw') return { outcome: 'draw', text: '무승부입니다.' };
  if (!winner) return { outcome: 'unknown', text: '게임 종료' };
  const winnerText = `${sideLabel(winner)} 승리`;
  if (mySide === winner) {
    return { outcome: 'win', text: `승리 - ${winnerText} (${reasonLabel(winReason, 'winner', winningLineLength)})` };
  }
  if (mySide) {
    return { outcome: 'lose', text: `패배 - ${winnerText} (${reasonLabel(winReason, 'loser', winningLineLength)})` };
  }
  return { outcome: 'spectator', text: `${winnerText} (${reasonLabel(winReason, 'spectator', winningLineLength)})` };
}

/** V70G8D — 남은 시간 표시 (mm:ss). 서버 마감 시각과 보정된 현재 시각 기준 */
export function formatTurnClock(seconds: number | null): string {
  if (seconds === null) return '';
  const s = Math.max(0, seconds);
  return `${String(Math.floor(s / 60)).padStart(2, '0')}:${String(s % 60).padStart(2, '0')}`;
}

export function remainingTurnSeconds(deadlineMs: number | null, serverNowMs: number, limitSec = 30): number | null {
  if (deadlineMs === null) return null;
  const left = Math.ceil((deadlineMs - serverNowMs) / 1000);
  return Math.max(0, Math.min(limitSec, left));
}

// ───────────── V70G8E — 금수 안내 / 진행 중 대국 확인 ─────────────

const FORBID_LABELS: Readonly<Record<string, string>> = {
  'double-three': '33 금수',
  'double-four': '44 금수',
  'overline': '장목 금수',
};
const KNOWN_FORBID_LABELS = new Set(Object.values(FORBID_LABELS));

/** 금수 안내 문구. 알 수 없는 코드는 내부 코드를 노출하지 않는다. */
export function forbiddenMoveText(code: string | null | undefined): string {
  const c = (code || '').trim();
  if (Object.prototype.hasOwnProperty.call(FORBID_LABELS, c)) return `금수입니다. (${FORBID_LABELS[c]})`;
  if (KNOWN_FORBID_LABELS.has(c)) return `금수입니다. (${c})`;
  return '금수입니다.';
}

interface CallableErrorLike {
  message?: string;
  details?: { reason?: string; forbidden?: string; activeRoomIds?: unknown } | null;
}

/**
 * 서버 오류 → 사용자 문구.
 *   금수는 details.forbidden(판정 코드)으로 한글 문구를 만들고,
 *   이전 서버 문구("금수입니다 (double-three).")가 와도 영문 코드를 숨긴다.
 */
export function toOmokErrorMessage(e: unknown): string {
  const err = (e || {}) as CallableErrorLike;
  const details = err.details || null;
  if (details && (details.reason === 'OMOK_FORBIDDEN' || typeof details.forbidden === 'string')) {
    return forbiddenMoveText(details.forbidden);
  }
  const msg = typeof err.message === 'string' && err.message ? err.message : String(e);
  const m = /^금수입니다\.?\s*\(([^)]*)\)\.?$/.exec(msg.trim());
  if (m) return forbiddenMoveText(m[1]);
  if (/^금수입니다/.test(msg.trim())) return '금수입니다.';
  return msg;
}

/** 진행 중 대국 때문에 입장이 거부된 오류면 해당 방 ID 목록, 아니면 null */
export function activeGameRoomIds(e: unknown): string[] | null {
  const details = ((e || {}) as CallableErrorLike).details;
  if (!details || details.reason !== 'OMOK_ACTIVE_GAME') return null;
  const ids = Array.isArray(details.activeRoomIds)
    ? details.activeRoomIds.filter((x): x is string => typeof x === 'string' && x.length > 0)
    : [];
  return ids.length > 0 ? ids : null;
}

/** 진행 중 안내 문구 */
export function turnMessage(
  isParticipant: boolean,
  isMyTurn: boolean,
  moveCount: number,
): string {
  if (!isParticipant) return `👀 관전 중 (이 방의 플레이어가 아닙니다) · ${moveCount}수`;
  return isMyTurn
    ? `🎯 당신의 차례입니다. · ${moveCount}수`
    : `⏳ 상대방 차례입니다. · ${moveCount}수`;
}
